// IndexedDB-backed warm cache for the JetStream 8h lake.
//
// Without a cache, every page load replays 8h × tenant data over the wire.
// At 50 systems × 1 msg/s × 8h × ~256 B that's ~100 MB on cellular for a
// max-size tenant — the deliberate cost agent3 v2 plan §13 Phase B
// acknowledged. This module closes that gap:
//
//   - queueWrite(envs)        async write-batch, no main-render cost
//   - hydrate(tenant)         pull last 8h from IDB on boot
//   - getWatermark(tenant)    so the consumer can warm-resume via
//                             OptStartSeq instead of cold replay
//   - evictExpired(tenant)    drop rows older than 8h on a 5-min cadence
//   - shouldUseCache()        quota probe — Safari/iOS fallback
//   - dropCache(tenant)       on logout / tenant switch
//
// Schema (per tenant):
//   db: lc-cache-${tenant}, version: 1
//   store rows  keyPath: streamSeq
//               index    by_machine_time  [systemId, deviceTimeMs]
//   store meta  keyPath: k
//               row      { k: 'watermark', streamSeq, serverTimeMs }
//
// Native IDB API (no idb / dexie wrapper) keeps the bundle flat. The
// promise wrapper below is small.

import type { JsEnvelope } from '@/nats/envelope';

const DB_VERSION   = 1;
const ROWS_STORE   = 'rows';
const META_STORE   = 'meta';
const WATERMARK_K  = 'watermark';
const EIGHT_HOURS_MS = 8 * 60 * 60 * 1000;

interface PersistedRow {
  streamSeq:    number;
  systemId:     string;
  eventTopic:   string;
  deviceTimeMs: number;
  serverTimeMs: number;
  /** Full JsEnvelope minus the redundant streamSeq/serverTimeMs (those
   *  live as top-level keys for indexing). */
  envelope:     JsEnvelope;
}

interface Watermark {
  k:            'watermark';
  streamSeq:    number;
  serverTimeMs: number;
}

// ── DB lifecycle ──────────────────────────────────────────────────────────

const openDbs = new Map<string, Promise<IDBDatabase>>();

function dbName(tenant: string): string {
  return `lc-cache-${tenant}`;
}

async function openDb(tenant: string): Promise<IDBDatabase> {
  let p = openDbs.get(tenant);
  if (p) return p;
  p = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(dbName(tenant), DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(ROWS_STORE)) {
        const rows = db.createObjectStore(ROWS_STORE, { keyPath: 'streamSeq' });
        rows.createIndex('by_machine_time', ['systemId', 'deviceTimeMs']);
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'k' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
  openDbs.set(tenant, p);
  return p;
}

/** Close the open DB handle for a tenant. Called on logout / tenant switch. */
export async function closeCache(tenant: string): Promise<void> {
  const p = openDbs.get(tenant);
  if (!p) return;
  openDbs.delete(tenant);
  try { (await p).close(); } catch { /* */ }
}

/** Permanently delete the IDB for a tenant. Use on logout if you want a
 *  fully clean slate; otherwise let evictExpired() age-trim instead. */
export async function dropCache(tenant: string): Promise<void> {
  await closeCache(tenant);
  await new Promise<void>((resolve) => {
    const req = indexedDB.deleteDatabase(dbName(tenant));
    req.onsuccess = () => resolve();
    req.onerror   = () => resolve();
    req.onblocked = () => resolve();
  });
}

// ── Quota probe ──────────────────────────────────────────────────────────

const MIN_FREE_BYTES = 200 * 1024 * 1024;

/** Coarse "is the cache worth trying?" check. Returns false on Safari/iOS
 *  with low quota or when IDB itself is unavailable. */
export async function shouldUseCache(): Promise<boolean> {
  if (typeof indexedDB === 'undefined') return false;
  if (!('storage' in navigator) || !navigator.storage?.estimate) {
    // Older Safari — try anyway, the worst case is we throw on first write.
    return true;
  }
  try {
    const { quota = 0, usage = 0 } = await navigator.storage.estimate();
    return quota - usage >= MIN_FREE_BYTES;
  } catch {
    return true;
  }
}

// ── Write batching ───────────────────────────────────────────────────────

const WRITE_BATCH       = 500;
const WRITE_DEBOUNCE_MS = 250;

interface WriteBuffer {
  tenant: string;
  pending: JsEnvelope[];
  timer: number | null;
}

let writeBuf: WriteBuffer | null = null;

/** Queue envelopes for async persistence. Caller passes a single tenant's
 *  worth — if a tenant switch happens, the prior buffer is flushed before
 *  the new tenant's writes start landing. */
export function queueWrite(tenant: string, envs: JsEnvelope[]): void {
  if (envs.length === 0) return;
  if (writeBuf && writeBuf.tenant !== tenant) {
    void flushNow();
  }
  if (!writeBuf) writeBuf = { tenant, pending: [], timer: null };
  for (const e of envs) writeBuf.pending.push(e);
  if (writeBuf.pending.length >= WRITE_BATCH) {
    void flushNow();
  } else if (writeBuf.timer == null) {
    writeBuf.timer = window.setTimeout(() => { void flushNow(); }, WRITE_DEBOUNCE_MS);
  }
}

async function flushNow(): Promise<void> {
  const buf = writeBuf;
  if (!buf) return;
  if (buf.timer != null) { clearTimeout(buf.timer); buf.timer = null; }
  if (buf.pending.length === 0) return;
  const batch = buf.pending;
  buf.pending = [];
  try {
    await persistBatch(buf.tenant, batch);
  } catch (err) {
    // Persist errors are non-fatal — cache is best-effort. Log so dev
    // can spot quota / schema issues.
    console.warn('[idb/cache] persist failed', err);
  }
}

async function persistBatch(tenant: string, envs: JsEnvelope[]): Promise<void> {
  const db = await openDb(tenant);
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction([ROWS_STORE, META_STORE], 'readwrite');
    const rowsStore = tx.objectStore(ROWS_STORE);
    let maxSeq = 0;
    let maxSeqServerTime = 0;
    for (const env of envs) {
      if (env.streamSeq <= 0) continue;
      const row: PersistedRow = {
        streamSeq:    env.streamSeq,
        systemId:     env.systemId,
        eventTopic:   env.eventTopic,
        deviceTimeMs: deviceTimeOf(env),
        serverTimeMs: env.serverTimeMs,
        envelope:     env,
      };
      rowsStore.put(row);
      if (env.streamSeq > maxSeq) {
        maxSeq           = env.streamSeq;
        maxSeqServerTime = env.serverTimeMs;
      }
    }
    if (maxSeq > 0) {
      const wm: Watermark = { k: WATERMARK_K, streamSeq: maxSeq, serverTimeMs: maxSeqServerTime };
      tx.objectStore(META_STORE).put(wm);
    }
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error);
    tx.onabort    = () => reject(tx.error);
  });
}

function deviceTimeOf(env: JsEnvelope): number {
  switch (env.payload.kind) {
    case 'sensor':    return env.payload.deviceTimeMs;
    case 'log':       return env.payload.deviceTimeMs;
    case 'heartbeat': return env.payload.deviceTimeMs;
    case 'alert':     return env.payload.deviceTimeMs;
    case 'batch':     return env.payload.readings.length > 0
                              ? env.payload.readings[env.payload.readings.length - 1].deviceTimeMs
                              : env.serverTimeMs;
  }
}

// ── Hydration ────────────────────────────────────────────────────────────

/** Pull last 8h of envelopes and the consumer watermark for a tenant.
 *  Returns nothing if cache is empty / unavailable. The returned envelopes
 *  are NOT sorted — the dedup map + ingest path absorb out-of-order
 *  inserts. Skipping the sort saves ~30ms on a 30k-row hydrate. */
export async function hydrate(tenant: string): Promise<{ envs: JsEnvelope[]; watermark: Watermark | null }> {
  const db = await openDb(tenant);
  const cutoff = Date.now() - EIGHT_HOURS_MS;

  const envs = await new Promise<JsEnvelope[]>((resolve) => {
    const out: JsEnvelope[] = [];
    const tx = db.transaction(ROWS_STORE, 'readonly');
    const idx = tx.objectStore(ROWS_STORE).index('by_machine_time');
    // Lower bound is "any systemId, deviceTimeMs >= cutoff". Use the
    // string-sort trick: key tuple comparison treats strings before
    // numbers in mixed-key indexes, but here both fields are
    // homogeneous within a row — IDB compares element-wise on the
    // composite. Empty-string lower bound lets every systemId through.
    const range = IDBKeyRange.lowerBound(['', cutoff]);
    const req = idx.openCursor(range);
    req.onsuccess = () => {
      const c = req.result;
      if (!c) return;
      const row = c.value as PersistedRow;
      if (row.deviceTimeMs >= cutoff) out.push(row.envelope);
      c.continue();
    };
    tx.oncomplete = () => resolve(out);
    tx.onerror    = () => resolve(out);
  });

  const watermark = await getMeta<Watermark>(db, WATERMARK_K);
  return { envs, watermark };
}

/** Cheap path used by the consumer-bootstrap effect to pick OptStartSeq
 *  before any hydration cost. */
export async function getWatermark(tenant: string): Promise<Watermark | null> {
  if (!(await shouldUseCache())) return null;
  const db = await openDb(tenant);
  return getMeta<Watermark>(db, WATERMARK_K);
}

function getMeta<T>(db: IDBDatabase, k: string): Promise<T | null> {
  return new Promise<T | null>((resolve) => {
    const tx = db.transaction(META_STORE, 'readonly');
    const req = tx.objectStore(META_STORE).get(k);
    req.onsuccess = () => resolve((req.result as T) ?? null);
    req.onerror   = () => resolve(null);
  });
}

// ── Eviction ─────────────────────────────────────────────────────────────

/** Delete rows older than 8h. Walked once on boot, then every 5 min while
 *  the tab stays alive. Cheap — single index range cursor. */
export async function evictExpired(tenant: string): Promise<number> {
  const db = await openDb(tenant);
  const cutoff = Date.now() - EIGHT_HOURS_MS;
  return new Promise<number>((resolve) => {
    let dropped = 0;
    const tx = db.transaction(ROWS_STORE, 'readwrite');
    const idx = tx.objectStore(ROWS_STORE).index('by_machine_time');
    // Walk every row; cursor is reasonably fast even at 30k rows. The
    // alternative (range upperBound on a composite key) is fragile against
    // IDB's mixed-key comparison rules.
    const req = idx.openCursor();
    req.onsuccess = () => {
      const c = req.result;
      if (!c) return;
      const row = c.value as PersistedRow;
      if (row.deviceTimeMs < cutoff) {
        c.delete();
        dropped++;
      }
      c.continue();
    };
    tx.oncomplete = () => resolve(dropped);
    tx.onerror    = () => resolve(dropped);
  });
}
